"use client";

import { FileType, type File, type VoteItem } from "@prisma/client";
import { type UploadFile } from "antd";
import { useEffect, useState } from "react";
import { getNamuFile, isOverMaxFileSize } from "~/utils/file";

export type NamuFile = {
  uid: string;
  id: number;
  fieldName: string;
  fileName: string;
  fileUrl: string;
  fileType: FileType;
};

export const useFileUpload = ({
  form,
  initialValues = {},
}: {
  form: any;
  initialValues?: any;
}) => {
  const [namuFiles, setNamuFiles] = useState<NamuFile[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && initialValues) {
      setIsInitialized(true);
      form?.setFieldsValue(initialValues);

      const initialFileList: any = [];

      const imageUrl = initialValues.imageUrl;

      if (imageUrl) {
        const imageFile: NamuFile = {
          uid: "imageUrl",
          id: 0,
          fieldName: "imageUrl",
          fileName: "imageUrl",
          fileUrl: imageUrl,
          fileType: FileType.IMAGE,
        };
        initialFileList.push(imageFile);
      }

      const voteItems = initialValues.voteItems;
      if (voteItems) {
        initialFileList.push(
          ...voteItems.map((voteItem: VoteItem, index: number) => {
            return {
              uid: index.toString(),
              id: voteItem.id ?? 0,
              fieldName: `option_image_${index}`,
              fileName: `option_image_${index}`,
              fileUrl: voteItem.imageUrl,
              fileType: FileType.IMAGE,
            } as NamuFile;
          }),
        );
      }

      const feedFiles = initialValues.feedFiles;
      if (feedFiles) {
        initialFileList.push(
          ...feedFiles.map((file: File, index: number) => {
            return {
              uid: index.toString(),
              id: 0,
              fieldName: `file_${index}`,
              fileName: `file_${index}`,
              fileUrl: file.url,
              fileType: file.fileType,
            } as NamuFile;
          }),
        );
      }

      setNamuFiles(initialFileList);
    }
  }, [initialValues, isInitialized]);

  const handleUpload = (namuFile: NamuFile) => {
    form.setFieldValue(namuFile.fieldName, namuFile.fileUrl);

    setNamuFiles((prevList: any) => {
      return [...prevList, namuFile];
    });
  };

  const handleRemove = (file: NamuFile) => {
    form.setFieldValue(file.fieldName, null);
    setNamuFiles((prevList) =>
      prevList.filter((item: NamuFile) => item.fieldName !== file.fieldName),
    );
  };

  const beforeUpload = (file: UploadFile, fieldName: string) => {
    if (isOverMaxFileSize(file)) {
      return false;
    }

    const namuFile = getNamuFile(file, fieldName);

    if (namuFile) {
      void handleUpload(namuFile);
    }

    return true;
  };

  return { namuFiles, setIsInitialized, handleRemove, beforeUpload };
};
